import { Config } from '@/environment';

export enum LogLevel {
  trace = 0,
  debug = 1,
  info = 2,
  warn = 3,
  error = 4,
}

export interface ConfigureLoggerOptions {
  userId: string;
}

/**
 * fields would be for optional structured data
 * e.g. { "selectedVariant": "Chicken Bowl", "MenuItemId": "CMG-5001" }
 */
export interface Logger {
  trace(message: string, fields?: object): void;
  debug(message: string, fields?: object): void;
  info(message: string, fields?: object): void;
  warn(message: string, fields?: object): void;
  error(message: string, fields?: object, exception?: any): void;
  unreachable(message: string, fields?: object): void;

  log(level: LogLevel, message: string, fields?: object, exception?: any): void;

  newInstance(name: string): Logger;
  configure(options: ConfigureLoggerOptions): void;
}

export abstract class AbstractLogger implements Logger {
  constructor(protected config: Config) {}

  trace(message: string, fields?: object) {
    this.log(LogLevel.trace, message, fields);
  }

  debug(message: string, fields?: object) {
    this.log(LogLevel.debug, message, fields);
  }

  info(message: string, fields?: object) {
    this.log(LogLevel.info, message, fields);
  }

  warn(message: string, fields?: object) {
    this.log(LogLevel.warn, message, fields);
  }

  error(message: string, fields?: object, exception?: any) {
    this.log(LogLevel.error, message, fields, exception);
  }

  unreachable(message: string, fields?: object) {
    this.log(LogLevel.error, `unreachable: ${message}`, fields);
  }

  log(level: LogLevel, message: string, fields?: object, exception?: any) {
    if (!this.shouldLog(level)) {
      return;
    }

    if (fields && this.config.logging.shouldVerifyFields) {
      try {
        const serializedFields = JSON.stringify(fields);

        if (this.config.logging.maxFieldsLength && serializedFields.length > this.config.logging.maxFieldsLength) {
          throw new Error(`Length of fields (${serializedFields.length}) exceeded max length of ${this.config.logging.maxFieldsLength}`);
        }
      } catch (e) {
        this.warn('Error serializing fields', e);

        fields = undefined;
      }
    }

    this.doLog(level, message, fields, exception);
  }

  configure(options: ConfigureLoggerOptions) {
    // Do nothing by default
  }

  abstract newInstance(name: string): Logger;
  protected abstract doLog(level: LogLevel, message: string, fields?: object, exception?: any): void;

  protected shouldLog(level: LogLevel): boolean {
    return this.config.logging.level <= level;
  }
}

export class ConsoleLogger extends AbstractLogger {
  constructor(private name: string, config: Config, private console: Console) {
    super(config);
  }

  doLog(level: LogLevel, message: string, fields?: object, exception?: any) {
    let effectiveLevel = LogLevel[level];

    if (!this.isSupportedLevel(effectiveLevel)) {
      this.doLog(LogLevel.warn, `Log level ${level} is not supported!`);

      effectiveLevel = 'error';
    }

    (this.console as any)[effectiveLevel](`[${this.name}] (${effectiveLevel}): ${message}`, fields ? fields : '', exception ? exception : '');
  }

  newInstance(name: string) {
    return new ConsoleLogger(name, this.config, this.console);
  }

  isSupportedLevel(level: string): level is keyof Console {
    return !!(this.console as any)[level];
  }
}
